import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {environment} from '../../../environments/environment';
import {CompaniesService} from '../../services/companies.service';
import {Observable, map} from 'rxjs';
import {ITimeSheet, ITimeSheetResponse} from '../models/ITimeSheet.interface';
import {ITimeSheetRequest} from '../models/ITimeSheetRequest.interface';
import {CorporatePermissions} from '../../enums/permissions/CorporatePermissions.enum';
import {ICustomerReportRequest} from '../models/ICustomerReportRequest.interface';
import {ITimeReportReq} from '../models/ITimeReportReq.interface';
import {ITimeReportCustomerRes} from '../models/ITimeReportCustomer.interface';
import {ITimeReportRes} from '../models/ITimeReportRes.interface';
import {CompanyUsersResponse} from '../../models/company/company-users-response.model';

@Injectable({
  providedIn: 'root'
})
export class TimeSheetService {

  private httpClient = inject(HttpClient);
  private _url = environment.apiConfig.corporateAPI.endpoint;
  private _identityUrl = environment.apiConfig.IdentityAPI.endpoint;
  private companyService = inject(CompaniesService);

  private get companyId() {
    return this.companyService.managedCompanyId;
  }

  private headers = new HttpHeaders({
    permission: CorporatePermissions.User
  });

  getCompanyUsers(companyId: number): Observable<CompanyUsersResponse> {
    const adminHeaders = new HttpHeaders({
      'permission': CorporatePermissions.Admin
    })
    return this.httpClient.get<CompanyUsersResponse>( `${this._identityUrl}/Companies/${this.companyId}/users`, {
      headers: adminHeaders
    });
  }

  getTimeSheet(startDate: string, endDate: string, userId: string): Observable<ITimeSheet> {
   const queryParams = new HttpParams()
     .set('startDate', startDate)
     .set('endDate', endDate)
     .set('userAzureObjectId', userId)

   return this.httpClient.get<ITimeSheet>(this._url + `/company/${this.companyId}/timesheets`, {
      params: queryParams,
      headers: this.headers
    });
  }

  createTimeSheet(sheet: ITimeSheetRequest): Observable<number> {
    return this.httpClient.post<number>(this._url + `/company/${this.companyId}/timesheets`, sheet, {
      headers: this.headers
    });
  }

  updateTimeSheet(id: number, sheet: ITimeSheetRequest): Observable<void> {
    return this.httpClient.put<void>(this._url + `/company/${this.companyId}/timesheets/` + id, sheet, {
       headers: this.headers
    });
  }

  deleteTimeSheet(id: number): Observable<void> {
    return this.httpClient.delete<void>(this._url + `/company/${this.companyId}/timesheets/` + id, {
       headers: this.headers
    });
  }

  //Reports

  getCustomerReport(req: ICustomerReportRequest): Observable<ITimeReportCustomerRes[]> {
    return this.httpClient.post<{departmentGroups: ITimeReportCustomerRes[]}>
    (this._url + `/company/${this.companyId}/timesheets/reports/per-customer`, req).pipe(
      map(res => res.departmentGroups)
    );
  }

  getEmployeeReport(objectId: string, date: ITimeReportReq): Observable<ITimeReportRes[]> {
    const queryParams = new HttpParams()
    .set('endDate', date.endDate)
    .set('startDate', date.startDate);
    return this.httpClient.get<{employeeReportRecords: ITimeReportRes[]}>
    (this._url + `/company/${this.companyId}/timesheets/reports/per-employee/${objectId}`, {
      params: queryParams
    }).pipe(
      map(res => res.employeeReportRecords)
    );
  }

  getDepartmentReport(departmentId: number, date: ITimeReportReq): Observable<ITimeReportRes[]> {
    const queryParams = new HttpParams()
    .set('endDate', date.endDate)
    .set('startDate', date.startDate);
    return this.httpClient.get<{departmentReportRecords: ITimeReportRes[]}>
    (this._url + `/company/${this.companyId}/timesheets/reports/per-department/${departmentId}`, {
      params: queryParams
    }).pipe(
      map(res => res.departmentReportRecords)
    );
  }

  getServiceReport(serviceId: number, date: ITimeReportReq): Observable<ITimeReportRes[]> {
    const queryParams = new HttpParams()
    .set('endDate', date.endDate)
    .set('startDate', date.startDate);
    return this.httpClient.get<{serviceReportRecords: ITimeReportRes[]}>
    (this._url + `/company/${this.companyId}/timesheets/reports/per-service/${serviceId}`, {
      params: queryParams
    }).pipe(
      map(res => res.serviceReportRecords)
    );
  }

  getTimesheetsByServiceId(serviceId: number): Observable<any[]> {
    return this.httpClient.get<any[]>(
      this._url + `/company/${this.companyId}/reports/timesheets/service/${serviceId}`,
      { headers: this.headers }
    );
  }
}
